import React, { useEffect } from "react";
import {
  Box,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { Theme, useTheme } from '@mui/material/styles'
import { createStyles, makeStyles } from '@mui/styles';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { GetSettlementInfoSpecific } from "../utils/GetCustomer";
import { useLocalStorage } from "../componentUtils/LocalStorage";
import { Bar } from "react-chartjs-2";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface SettlementHistory {
  shipmentNum: string,
  equipmentNum:string,
  route: string,
  visitDt: Date,
  meterReading: number,
  totalizerReading: number,
  lastMeterReading: number,
  lastTotalizerReading: number,
  oos: number,
  fillRate: number,
  txSettlementItems: any,
}

interface MatDesc {
  materialNum: string,
  materialNumShort: string,
  description: string
}

interface MaterialData {
  materialNum: string,
  description: string,
  qty: number[]
}

interface StockPrediction {
  labels: String[],
  materialData: MaterialData[]
}

interface SettlementState {
  isLoaded: boolean;
  settlement: SettlementHistory[];
  matDesc: MatDesc[];
  visitPlanning: any;
  stockPrediction: any;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    divWidth: {
      width: "100%",
    },
    gridSpaceable: {
      padding: theme.spacing(2),
    },
    gridMarginTop: {
      marginTop: theme.spacing(2),
    },
    chipActual: {
      marginTop: theme.spacing(2),
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(2)
    },
    mapContainer: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  })
);

const useRowStyles = makeStyles({
  root: {
    "& > *": {
      borderBottom: "unset",
    },
  },
});

export default function SettlementInfo(params:{CustomerNum:string|undefined}) {
  let CustomerNum = params.CustomerNum||'';
  const classes = useStyles();
  const theme = useTheme();
  const [state, setState] = React.useState<SettlementState>({
    isLoaded: false,
    settlement: [],
    matDesc: [],
    visitPlanning: null,
    stockPrediction: null
  });
  const [salesOrg,] = useLocalStorage("SETTINGS.salesOrg", "");

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true
    },
    layout: {
      padding: 0
    },
    scales: {
      xAxes: [
        { stacked: true }
      ],
      yAxes: [
        {
          stacked: true,
          gridLines: {
            borderDash: [2],
            borderDashOffset: [2],
            color: theme.palette.divider,
            drawBorder: false,
            zeroLineBorderDash: [2],
            zeroLineBorderDashOffset: [2],
            zeroLineColor: theme.palette.divider
          }
        }
      ]
    }
  }

  function colorPal(num: number):Array<string> {
    const colors = ['#1f77b4', '#aec7e8', '#ff7f0e', '#ffbb78', "2ca02c",
                    '#98df8a', '#d62728', '#ff9896', '#9467db', '#c5b0d5',
                    '#8c564b', '#c49c94', '#e377c2', '#f7b6d2', '#7f7f7f',
                    '#c7c7c7', '#bcbd22', '#dbdb8d', '#17becf', '#9edae5'];
    var pal = Array<string>();
    for(var i=0; i<num; i++) {
      let index = (i % colors.length);
      pal[i] = colors[index];
    }
    return pal;
  }

  useEffect(() => {
    if (state.isLoaded) return;

    GetSettlementInfoSpecific(salesOrg.toString(), CustomerNum).then(
      (v: any) => {
          var predData: StockPrediction = v.stockPrediction;
          var preddata: any = null;
          if (predData) {
            const colors2 = colorPal(predData.materialData.length);
            var datasets = new Array<any>();
            const matDescArr:Array<MatDesc> = v.matDesc;
            predData.materialData.forEach((element, i) => {
              var desc = "";
              const matDesc = matDescArr.find(elem => elem.materialNum === element.materialNum);
              if (matDesc) {
                desc = matDesc.description;
              }
              const shortNum = element.materialNum.replace(/^0+/, "");
              let label = shortNum + " - " + desc;
              datasets[i] = {label: label, data: element.qty, backgroundColor: colors2[i]};
            })
            preddata = {labels: predData.labels, datasets: datasets};
          }

        setState({
          ...state,
          isLoaded: true,
          settlement: v.settlement,
          matDesc: v.matDesc,
          visitPlanning: v.visitPlanning,
          stockPrediction: preddata
        });
      }
    );
  }, [state, CustomerNum]);

  const Row = (props: any) => {
    const { row } = props;
    const [open, setOpen] = React.useState(false);
    const classes = useRowStyles();
    var meterReading:string = "" + row.meterReading;
    var totalizerReading:string = "" + row.totalizerReading.toFixed(2);

    if (row.lastMeterReading > 0) {
      const diff = row.meterReading - row.lastMeterReading;
      meterReading = meterReading + " (+" + diff + ")";
    }
    if (row.lastTotalizerReading > 0) {
      const diff = row.totalizerReading - row.lastTotalizerReading;
      totalizerReading = totalizerReading + " (+" + diff.toFixed(2) + ")";
    }

    return (
      <React.Fragment>
        <TableRow className={classes.root}>
          <TableCell>
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell component="th" scope="row">
            {row.visitDt}
          </TableCell>
          <TableCell align="right">{row.equipmentNum}</TableCell>
          <TableCell align="right">{row.shipmentNum}</TableCell>
          <TableCell align="right">{row.route}</TableCell>
          <TableCell align="right">{row.visitNum}</TableCell>
          <TableCell align="left">{meterReading}</TableCell>
          <TableCell align="left">{totalizerReading}</TableCell>        
          <TableCell align="right">{row.cashCollected.toFixed(2)}</TableCell>
          <TableCell align="right">{row.oos}</TableCell>           
          <TableCell align="right">{row.fillRate}%</TableCell>           
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box margin={1}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="right">Material Number</TableCell>
                      <TableCell align="right">Description</TableCell>
                      <TableCell align="right">Fill Qty</TableCell>
                      <TableCell align="right">Closing Inventory</TableCell>
                      <TableCell align="right">Vend Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.txSettlementItems.map((item: any, index: number) => {
                      var desc = "";
                      const matDesc = state.matDesc.find(elem => elem.materialNum === item.materialNum);
                      if (matDesc) {
                        desc = matDesc.description;
                      }
                      return (
                      <TableRow key={index} selected={item.quantity != 0}>
                        <TableCell component="th" scope="row" align="right">
                          {item.materialNum.replace(/^0+/, "")}
                        </TableCell>
                        <TableCell align="right">
                          {desc}
                        </TableCell>                        
                        <TableCell align="right">
                          {item.quantity === item.closingInventory?(<ErrorOutlineIcon titleAccess="Out Of Stock" color="primary" fontSize="small" />):''}
                          &nbsp;{item.quantity}&nbsp;{item.uom}
                        </TableCell>
                        <TableCell align="right">
                          {item.closingInventory}
                        </TableCell>
                        <TableCell align="right">
                          {item.vendPrice.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    )})}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  if (!state.isLoaded) return <div>Loading...</div>;

  var driAtFill = 0;
  if (state.visitPlanning && state.visitPlanning.critical_material_inventory > 0 && state.visitPlanning.critical_material_consumption > 0) {
    driAtFill = Math.floor(state.visitPlanning.critical_material_inventory / state.visitPlanning.critical_material_consumption);
  }

  return (
    <div className={classes.root}>
      <Chip
        label="Last Settlements"
        className={classes.chipActual}
        color="primary"
      />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Visit Date</TableCell>
              <TableCell align="right">Equipment No</TableCell>
              <TableCell align="right">Shipment</TableCell>
              <TableCell align="right">Route</TableCell>
              <TableCell align="right">Visit</TableCell>
              <TableCell align="left">Meter</TableCell>
              <TableCell align="left">Totalizer</TableCell>
              <TableCell align="right">Cash Collected</TableCell>
              <TableCell align="right">Materials out-of-stock</TableCell>
              <TableCell align="right">Fill rate</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.settlement.map((item, index) => {
              if ((index + 1) < state.settlement.length) {
                const next = state.settlement[index + 1];
                item.lastTotalizerReading = next.totalizerReading;
                item.lastMeterReading = next.meterReading;                
              } else {
                item.lastTotalizerReading = 0;
                item.lastMeterReading = 0;
              }
              // fill rate and out-of-stocks
              var totalFillUnits = 0;
              var totalClosingInv = 0;
              var totalOOS = 0;
              var fillRate = 0;
              item.txSettlementItems.map((sitem: any, index: number) => {
                totalFillUnits += sitem.quantity;
                totalClosingInv += sitem.closingInventory;
                if (sitem.quantity >= sitem.closingInventory) {
                  //Fix:844711-Handling oos count on initial fill event when fill qty & closing qty are 0
                  if(!(sitem.quantity===0&&sitem.closingInventory===0)){
                    totalOOS++;
                  }
                }
              });
              if (totalClosingInv > 0) {
                fillRate = Math.ceil((totalFillUnits / totalClosingInv) * 100);
              }
              item.fillRate = fillRate;
              item.oos = totalOOS;
              return (
              <Row key={index} row={item} />
            )})}
          </TableBody>
        </Table>
      </TableContainer>
      {!state.visitPlanning ||
      <div>
      <Chip
        label="Visit Planning"
        className={classes.chipActual}
        color="primary"
      />
      <Grid container spacing={2} className={classes.gridMarginTop}>
        <Grid item xs={3} lg={4} className={classes.gridSpaceable}>
          <TextField fullWidth={true} InputProps={{readOnly: true,}}
            label="Last Visit Date"
            value={state.visitPlanning.last_visit_dt}
            defaultValue={state.visitPlanning.last_visit_dt}
          />
        </Grid>
        <Grid item xs={3} lg={4} className={classes.gridSpaceable}>
          <TextField fullWidth={true} InputProps={{readOnly: true,}}
            label="Last Total Closing Inventory"
            value={state.visitPlanning.material_inventory}
            defaultValue={state.visitPlanning.material_inventory}
          />
        </Grid>            
        <Grid item xs={3} lg={4} className={classes.gridSpaceable}>
          <TextField fullWidth={true} InputProps={{readOnly: true,}}
            label="Last Filled Units"
            value={state.visitPlanning.last_fill_units}
            defaultValue={state.visitPlanning.last_fill_units}
          />
        </Grid>
        <Grid item xs={3} lg={4} className={classes.gridSpaceable}>
          <TextField fullWidth={true} InputProps={{readOnly: true,}}
            label="Last Fill Rate"
            value={state.visitPlanning.last_fill_rate ? (state.visitPlanning.last_fill_rate * 100).toFixed(0) + "%" : ""}
            defaultValue={state.visitPlanning.last_fill_rate ? (state.visitPlanning.last_fill_rate * 100).toFixed(0) + "%" : ""}
          />
        </Grid>
        <Grid item xs={3} lg={4} className={classes.gridSpaceable}>
          <TextField fullWidth={true} InputProps={{readOnly: true,}}
            label="Next Planned Visit Date"
            value={state.visitPlanning.next_visit_dt_plan}
            defaultValue={state.visitPlanning.next_visit_dt_plan}
          />
        </Grid>        
        <Grid item xs={3} lg={4} className={classes.gridSpaceable}>
          <TextField fullWidth={true} InputProps={{readOnly: true,}}
            label="Next Planned Route"
            value={state.visitPlanning.route_plan}
            defaultValue={state.visitPlanning.route_plan}
          />
        </Grid>
        <Grid item xs={3} lg={4} className={classes.gridSpaceable}>
          <TextField fullWidth={true} InputProps={{readOnly: true,}}
            label="Material Consumption per Sales Day"
            value={state.visitPlanning.material_consumption ? state.visitPlanning.material_consumption.toFixed(2) : ""}
            defaultValue={state.visitPlanning.material_consumption ? state.visitPlanning.material_consumption.toFixed(2) : ""}
          />
        </Grid>
        <Grid item xs={3} lg={4} className={classes.gridSpaceable}>
          <TextField fullWidth={true} InputProps={{readOnly: true,}}
            label="Critical Material"
            value={state.visitPlanning.critical_material_num ? state.visitPlanning.critical_material_num.replace(/^0+/, "") : ""}
            defaultValue={state.visitPlanning.critical_material_num ? state.visitPlanning.critical_material_num.replace(/^0+/, "") : ""}
          />
        </Grid>                     
        <Grid item xs={3} lg={4} className={classes.gridSpaceable}>
          <TextField fullWidth={true} InputProps={{readOnly: true,}}
            label="Last Critical Material Closing Inventory"
            value={state.visitPlanning.critical_material_inventory}
            defaultValue={state.visitPlanning.critical_material_inventory}
          />
        </Grid>
        <Grid item xs={3} lg={4} className={classes.gridSpaceable}>
          <TextField fullWidth={true} InputProps={{readOnly: true,}}
            label="Critical Material Consumtion per Sales Day"
            value={state.visitPlanning.critical_material_consumption ? state.visitPlanning.critical_material_consumption.toFixed(2) : ""}
            defaultValue={state.visitPlanning.critical_material_consumption ? state.visitPlanning.critical_material_consumption.toFixed(2) : ""}
          />
        </Grid>
        <Grid item xs={3} lg={4} className={classes.gridSpaceable}>
          <TextField fullWidth={true} InputProps={{readOnly: true,}}
            label="DRI at Fill"
            value={driAtFill}
            defaultValue={driAtFill}
          />
        </Grid>
        <Grid item xs={3} lg={4} className={classes.gridSpaceable}>
          <TextField fullWidth={true} InputProps={{readOnly: true,}}
            label="Last Inventory Upate"
            value={state.visitPlanning.last_inventory_update_dt}
            defaultValue={state.visitPlanning.last_inventory_update_dt}
          />
        </Grid>
        <Grid item xs={3} lg={4} className={classes.gridSpaceable}>
          <TextField fullWidth={true} InputProps={{readOnly: true,}}
            label="Last Prediction Upate"
            value={state.visitPlanning.last_update_dt}
            defaultValue={state.visitPlanning.last_update_dt}
          />
        </Grid>               
      </Grid>

      <Chip
        label="Visit Planning Items"
        className={classes.chipActual}
        color="primary"
      />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="right">Material Number</TableCell>
              <TableCell align="right">Description</TableCell>
              <TableCell align="right"># Records for Avg</TableCell>
              <TableCell align="right">Avg Consumption</TableCell>
              <TableCell align="right">Trend</TableCell>              
              <TableCell align="right">Closing Inventory</TableCell>           
              <TableCell align="right">Last Known Inventory</TableCell>              
            </TableRow>
          </TableHead>
          <TableBody>
          {state.visitPlanning.txVisitPlanningItems.map((item: any, index: number) => {
            var desc = "";
            const matDesc = state.matDesc.find(elem => elem.materialNum === item.material_num);
            if (matDesc) {
              desc = matDesc.description;
            }
            return (
              <TableRow key={index}>
                <TableCell align="right">{item.material_num.replace(/^0+/, "")}</TableCell>
                <TableCell align="right">{desc}</TableCell>
                <TableCell align="right">{item.average_based_on_rec}</TableCell>
                <TableCell align="right">{item.average_consumption.toFixed(2)}</TableCell>
                <TableCell align="right">{item.average_trend.toFixed(2)}</TableCell>
                <TableCell align="right">{item.init_inventory}</TableCell>
                <TableCell align="right">{item.last_inventory}</TableCell>                                                             
              </TableRow>
            )})}
          </TableBody>
        </Table>
      </TableContainer>

      <Chip
        label="Stock Prediction"
        className={classes.chipActual}
        color="primary"
      />
            <Bar type="bar" height={50} options={options} data={state.stockPrediction} />

      </div>
      }
    </div>
  );
}
